import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { AddLocations, CreateLocationSuccess, DeleteLocation, SetLocations } from 'app/library/location/actions';
import { MaxBrainLocation } from 'app/library/location/models/location.model';
import { LocationState } from 'app/library/location/models/location.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { DeselectSelectedLocations, SearchLocations, SelectFilteredLocations, ToggleSelectedLocation } from '../actions';
import { LocationsStateModel } from './locations.state-model';

@State<LocationsStateModel>({
    name: 'locations',
    defaults: new LocationsStateModel(),
})
@Injectable()
export class LocationsState extends SubentityState<MaxBrainLocation> {
    @Selector([LocationState.getEntities])
    static getFilteredEntities(state: LocationsStateModel, locations: MaxBrainLocation[]): MaxBrainLocation[] {
        if (!locations) {
            return null;
        }

        return locations
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, MaxBrainLocation], b: [number, MaxBrainLocation]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, MaxBrainLocation]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: LocationsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: LocationsStateModel): string {
        return state.searchTerm;
    }

    constructor(private store: Store, private action$: Actions) {
        super(null, 0);

        this._propstoSearch = [...MaxBrainLocation.props_to_search];
    }

    @Action(AddLocations)
    addEntities(ctx: StateContext<LocationsStateModel>, action: AddLocations): void {
        const locations = this.store.selectSnapshot(LocationState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, locations);
    }

    @Action(CreateLocationSuccess)
    createEntity(ctx: StateContext<LocationsStateModel>, action: CreateLocationSuccess): void {
        const locations = this.store.selectSnapshot(LocationState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, locations);
    }

    @Action(DeleteLocation)
    deleteEntity({ getState, patchState }: StateContext<LocationsStateModel>, { payload }: DeleteLocation): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(SetLocations)
    setEntities(ctx: StateContext<LocationsStateModel>, action: SetLocations): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedLocation)
    toggleSelect(ctx: StateContext<LocationsStateModel>, action: ToggleSelectedLocation): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredLocations)
    selectAll(ctx: StateContext<LocationsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedLocations)
    deselectAll(ctx: StateContext<LocationsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchLocations)
    search(ctx: StateContext<LocationsStateModel>, action: SearchLocations): void {
        const locations = this.store.selectSnapshot(LocationState.getEntities) || [];

        this._searchSubentities(ctx, action, locations);
    }
}
